import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

interface MediaPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'image' | 'video';
  src: string;
  alt?: string;
}

/**
 * 通用媒体预览弹窗组件
 * 支持图片和视频预览，ESC 键关闭，点击遮罩关闭
 */
export default function MediaPreviewModal({
  isOpen,
  onClose,
  type,
  src,
  alt
}: MediaPreviewModalProps) {
  const { t } = useTranslation(['common_ui']);
  const resolvedAlt = alt ?? t('common_ui:mediaPreview.preview');
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !src) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-[9999]"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="relative w-full h-full flex items-center justify-center">
        {type === 'image' ? (
          <img
            src={src}
            alt={resolvedAlt}
            className="max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <video
            src={src}
            controls
            autoPlay
            className="max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/75 rounded-full p-2 transition-colors"
          aria-label={t('common_ui:mediaPreview.close')}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
}
